import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ActiveAssessmentPrefill,
  Assessment,
  AssessmentPagination,
  Goal,
  SubGoal,
} from '../../types/goal';
import moment from 'moment';

const defaultPagination: AssessmentPagination = {
  page: 1,
  per_page: 3,
  totalRecords: 0,
  totalPages: 0,
};

const parseAssessmentNumber = (id: string): number => {
  const match = id.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
};

const isSkippedAssessment = (assessment?: Assessment): boolean =>
  assessment?.action?.toLowerCase() === "skipped" || Boolean(assessment?.isSkip);

const getLastUnskippedAssessment = (
  assessments: Record<string, Assessment>
): Assessment | undefined =>
  Object.values(assessments)
    .filter((a) => !a.isNew && !isSkippedAssessment(a))
    .sort(
      (a, b) => parseAssessmentNumber(b.id) - parseAssessmentNumber(a.id)
    )[0];

const getSubGoalValue = (subGoal: SubGoal, assessmentId: string): string => {
  const cell = subGoal[assessmentId] as Assessment | undefined;
  return cell?.value || "";
};

const getPrefillValue = (
  prefill: ActiveAssessmentPrefill | null | undefined,
  goalId: string | number,
  subGoalId: number
): string => {
  if (!prefill) return "";

  const goalPrefill = prefill?.goals?.find(
    (goal) => goal?.goalId?.toString() === goalId.toString()
  );
  const subGoalPrefill = goalPrefill?.subGoals?.find(
    (subGoal) => subGoal?.id === subGoalId
  );

  return subGoalPrefill?.value || "";
};

const resolveNewAssessmentValue = (
  subGoal: SubGoal,
  goalId: string | number,
  assessmentId: string | undefined,
  prefill: ActiveAssessmentPrefill | null | undefined,
  isDiagnostics: boolean
): { value: string; isPrefilled: boolean; isEditable: boolean; isInherited?: boolean } => {
  // Diagnostics: start empty until a lab report is uploaded/applied
  if (isDiagnostics) {
    return { value: "", isPrefilled: false, isEditable: true };
  }

  if (assessmentId) {
    const value = getSubGoalValue(subGoal, assessmentId);
    if (value) {
      return { value, isPrefilled: false, isEditable: true };
    }
  }

  const prefillValue = getPrefillValue(prefill, goalId, subGoal.id);
  if (prefillValue) {
    return { value: prefillValue, isPrefilled: true, isEditable: true };
  }

  return { value: "", isPrefilled: false, isEditable: true };
};

interface GoalBucket {
  goals: { [key: string]: Goal };
  assessments: { [key: string]: Assessment };
  totalAssessment: number;
  assessmentTrend: any[];
  pagination: AssessmentPagination;
  activeAssessmentPrefill: ActiveAssessmentPrefill | null;
}

interface GoalState {
  data: {
    mainGoal: GoalBucket;
    customGoals: GoalBucket;
  };
  preAssessments: any[];
}

const initialState: GoalState = {
  data: {
    mainGoal: {
      goals: {},
      assessments: {},
      totalAssessment: 0,
      assessmentTrend: [],
      pagination: { ...defaultPagination },
      activeAssessmentPrefill: null,
    },
    customGoals: {
      goals: {},
      assessments: {},
      totalAssessment: 0,
      assessmentTrend: [],
      pagination: { ...defaultPagination },
      activeAssessmentPrefill: null,
    }
  },
  preAssessments: []
};

const goalSlice = createSlice({
  name: 'goals',
  initialState,
  reducers: {
    addAssessment: (state, action) => {
      const goalType = action.payload?.isPrimary ? 'mainGoal' : 'customGoals';
      const goalBucket = state.data[goalType];
      if (!goalBucket?.goals) return;

      let goals: Record<string, Goal>;
      try {
        goals = JSON.parse(JSON.stringify(goalBucket.goals)) as Record<string, Goal>;
      } catch (error) {
        console.error("Failed to clone goals:", error);
        return;
      }
      
      // Find max assessment ID
      let maxId = Object.keys(goalBucket.assessments ?? {})
        .map(id => parseInt(id.split('_')[1], 10))
        .reduce((max, current) => Math.max(max, current), 0);
      if(!maxId) {
        maxId = 0;
      }
      const newId = `'assessment'_${maxId + 1}`;

      const lastUnskipped = getLastUnskippedAssessment(
        goalBucket.assessments ?? {}
      );
      const activeAssessmentPrefill = goalBucket.activeAssessmentPrefill;

      const newAssessment: Assessment = {
        id: newId,
        isNew: true,
        name: 'New Assessment',
      };
      // Create assessment trend structure
      const assessmentTrend = Object.keys(goals).map(goalId => {
        const goal = goals[goalId];
        if (!goal) return { goal_id: goalId, subGoals: [] };

        return {
          goal_id: goalId,
          subGoals: goal.subGoals?.map((subGoal: SubGoal) => {
            if (!subGoal) return null;

            const { value } = resolveNewAssessmentValue(
              subGoal,
              goalId,
              lastUnskipped?.id,
              activeAssessmentPrefill,
              action.payload?.type === "diagnostics"
            );

            return {
              sub_goal_id: subGoal.id,
              [`${newId}`]: {
                value,
                isNew: true,
                delta: {
                  type: '',
                }
              }
            };
          }).filter(Boolean) || []
        };
      });

      // Update assessment trend in state
      if (!state.data[goalType]) return;
      state.data[goalType].assessmentTrend = assessmentTrend;

      // Rest of the existing addAssessment logic
      state.data[goalType].assessments = {
        [newId]: newAssessment,
        ...Object.fromEntries(
          Object.entries(goalBucket.assessments ?? {}).map(([id, assessment]) => [
            id,
            { ...assessment, isNew: false }
          ])
        )
      };

      Object.keys(goals).forEach(goalId => {
        const goal = goals[goalId];
        if (!goal) return;

        if (goal.subGoals) {
          const updatedSubGoals = goal.subGoals.map((subGoal: SubGoal) => {
            if (!subGoal) return subGoal;

            const { value, isPrefilled, isEditable, isInherited } =
              resolveNewAssessmentValue(
              subGoal,
              goalId,
              lastUnskipped?.id,
              activeAssessmentPrefill,
              action.payload?.type === "diagnostics"
            );

            return {
              ...subGoal,
              [newId]: {
                id: newId,
                value,
                date: "",
                ...(isPrefilled ? { isPrefilled: true } : {}),
                isEditable: activeAssessmentPrefill?.isEditable ?? isEditable,
                isInherited: isInherited ?? false,
              } satisfies Assessment,
            };
          });
          
          if (!state.data[goalType]?.goals) return;

          state.data[goalType].goals[goalId] = {
            ...goal,
            // status: 'pending',
            subGoals: updatedSubGoals as SubGoal[],
            assessmentIds: [newId, ...(goal.assessmentIds || [])]
          };
        } else {
          if (!state.data[goalType]?.goals) return;

          state.data[goalType].goals[goalId] = {
            ...goal,
            // status: 'pending',
            assessmentIds: [newId, ...(goal.assessmentIds || [])]
          };
        }
      });
      if (state.data[goalType]) {
        state.data[goalType].totalAssessment = (state.data[goalType].totalAssessment ?? 0) + 1;
      }
    },
    totalAssessmentCount: (state, action: PayloadAction<{count: number, isPrimary: boolean}>) => {
        const goalType = action.payload.isPrimary ? 'mainGoal' : 'customGoals';
        state.data[goalType].totalAssessment = action.payload.count;
    },
    setGoalsPagination: (state, action: PayloadAction<{
      pagination: AssessmentPagination;
      isPrimary: boolean;
    }>) => {
      const goalType = action.payload?.isPrimary ? 'mainGoal' : 'customGoals';
      if (!state.data[goalType]) return;
      state.data[goalType].pagination = action.payload?.pagination ?? defaultPagination;
    },
    setActiveAssessmentPrefill: (state, action: PayloadAction<{
      prefill: ActiveAssessmentPrefill | null;
      isPrimary: boolean;
    }>) => {
      const goalType = action.payload?.isPrimary ? 'mainGoal' : 'customGoals';
      if (!state.data[goalType]) return;
      state.data[goalType].activeAssessmentPrefill = action.payload?.prefill ?? null;
    },
    applyActiveAssessmentPrefill: (state, action: PayloadAction<{ isPrimary: boolean }>) => {
      const goalType = action.payload.isPrimary ? 'mainGoal' : 'customGoals';
      const prefill = state.data[goalType]?.activeAssessmentPrefill;
      if (!prefill?.goals?.length) return;

      const newAssessment = Object.values(
        state.data[goalType]?.assessments ?? {}
      ).find(
        (assessment) => assessment?.isNew || assessment?.action === "preview"
      );
      if (!newAssessment?.id) return;

      Object.keys(state.data[goalType]?.goals ?? {}).forEach((goalId) => {
        const goal = state.data[goalType]?.goals?.[goalId];
        if (!goal?.subGoals?.length) return;

        const updatedSubGoals = goal.subGoals.map((subGoal) => {
          if (!subGoal?.id) return subGoal;

          const cell = subGoal[newAssessment.id] as Assessment | undefined;
          if (cell?.value) return subGoal;

          const prefillValue = getPrefillValue(prefill, goalId, subGoal.id);
          if (!prefillValue) return subGoal;

          return {
            ...subGoal,
            [newAssessment.id]: {
              ...cell,
              id: newAssessment.id,
              value: prefillValue,
              isPrefilled: true,
              isEditable: prefill?.isEditable ?? true,
              isInherited: false,
            } satisfies Assessment,
          };
        });

        if (!state.data[goalType]?.goals) return;

        state.data[goalType].goals[goalId] = {
          ...goal,
          subGoals: updatedSubGoals as SubGoal[],
        };
      });
    },
    updateSubGoalAssessment: (state, action: PayloadAction<{
      value: string;
      goalId: string;
      subGoalId: string;
      assessmentId: string;
      isPrimary: boolean;
    }>) => {
      const { value, goalId, subGoalId, assessmentId, isPrimary } = action.payload;
      const goalType = isPrimary ? 'mainGoal' : 'customGoals';
      const goal = state.data[goalType].goals[goalId];
      
      if (goal && goal.subGoals) {
        // Find current value from assessmentTrend
        const goalTrend = state.data[goalType].assessmentTrend.find(g => g.goal_id === goalId);
        const subGoalTrend = goalTrend?.subGoals.find((sg: { sub_goal_id: string | number }) => 
          sg.sub_goal_id.toString() === subGoalId.toString()
        );
        const currentValue = subGoalTrend?.[`${assessmentId}`]?.value;

        const updatedSubGoals = goal.subGoals.map(subGoal => {
          if (subGoal.id.toString() === subGoalId) {
            const currentValueIndex = Array.isArray(subGoal.answers?.options) 
              ? subGoal.answers.options.indexOf(currentValue)
              : -1;
            // Find new value index
            const newValueIndex = Array.isArray(subGoal.answers?.options)
              ? subGoal.answers.options.indexOf(value)
              : -1;
            
            // Determine trend
            const trend = newValueIndex !== -1 && currentValueIndex !== -1 ? newValueIndex === currentValueIndex ? ''
              : newValueIndex < currentValueIndex ? '+' : '-'
              : undefined;
            
            return {
              ...subGoal,
              [assessmentId]: {
                value: value,
                date: '',
                delta: {
                  type: trend,
                }
              }
            };
          }
          return subGoal;
        });
        
        state.data[goalType].goals[goalId] = {
          ...goal,
          subGoals: updatedSubGoals as any
        };
      }
    },
    cancelAssessment: (state, action: PayloadAction<{ isPrimary: boolean }>) => {
      const goalType = action.payload.isPrimary ? 'mainGoal' : 'customGoals';
      // Find assessment to be removed
      const assessmentToRemove = Object.values(state.data[goalType].assessments)
        .find(assessment => assessment.isNew)?.id;

      // Remove assessment
      state.data[goalType].assessments = Object.fromEntries(
        Object.entries(state.data[goalType].assessments)
          .filter(([_, assessment]) => !assessment.isNew)
      );

      // Remove assessment reference and data from goals
      if (assessmentToRemove) {
        Object.keys(state.data[goalType].goals).forEach(goalId => {
          const goal = state.data[goalType].goals[goalId];
          
          // Update subgoals to remove assessment data
          if (goal.subGoals) {
            const updatedSubGoals = goal.subGoals.map(subGoal => {
              const { [assessmentToRemove]: removed, ...rest } = subGoal;
              return rest;
            });
            
            state.data[goalType].goals[goalId] = {
              ...goal,
              subGoals: updatedSubGoals as any,
              // status: 'pending',
              assessmentIds: goal.assessmentIds?.filter(id => id !== assessmentToRemove)
            };
          } else {
            state.data[goalType].goals[goalId] = {
              ...goal,
              // status: 'pending',
              assessmentIds: goal.assessmentIds?.filter(id => id !== assessmentToRemove)
            };
          }
        });
      }
      state.data[goalType].totalAssessment = state.data[goalType].totalAssessment - 1;
    },
    markAsSkip: (state, action: PayloadAction<{
      assessmentId: string;
      isPrimary: boolean;
    }>) => {
      const { assessmentId, isPrimary } = action.payload;
      const goalType = isPrimary ? 'mainGoal' : 'customGoals';
      
      // Update the assessment state
      if (state.data[goalType].assessments[assessmentId]?.isNew) {
        state.data[goalType].assessments[assessmentId] = {
          ...state.data[goalType].assessments[assessmentId],
          isSkip: true,
          action: "skipped",
        };
      }
      
      // Update goals and subgoals (existing logic)
      Object.keys(state.data[goalType].goals).forEach(goalId => {
        const goal = state.data[goalType].goals[goalId];
        if (goal.subGoals) {
          const updatedSubGoals = goal.subGoals.map(subGoal => ({
            ...subGoal,
            [assessmentId]: { value: '', date: '', isSkip: true, delta: { type: '' } }
          }));
          
          state.data[goalType].goals[goalId] = {
            ...goal,
            subGoals: updatedSubGoals as any
          };
        }
      });
      state.data[goalType].assessments[assessmentId] = {
        ...state.data[goalType].assessments[assessmentId],
        date: moment(new Date()).format("YYYY-MM-DD")
      };
    },
    markAsComplete: (state, action: PayloadAction<{
      goalId: string;
      isPrimary: boolean;
    }>) => {
      const { goalId, isPrimary } = action.payload;
      const goalType = isPrimary ? 'mainGoal' : 'customGoals';
      const goal = state.data[goalType].goals[goalId];
      // Find the new assessment
      const newAssessment = Object.values(state.data[goalType].assessments)
        .find(assessment => assessment.isNew);
      if (goal.subGoals && newAssessment) {
        const goalTrend = state.data[goalType].assessmentTrend.find(g => g.goal_id.toString() === goalId.toString());
        const updatedSubGoals = goal.subGoals.map(subGoal => {
          const options = subGoal.answers.options;
          const subGoalTrend = goalTrend?.subGoals.find((sg: { sub_goal_id: string | number }) => 
            sg.sub_goal_id.toString() === subGoal.id.toString()
          );
          const currentValue = subGoalTrend?.[`${newAssessment.id}`]?.value;
          const currentValueIndex = options.indexOf(currentValue);
          return (
            {
            ...subGoal,
            [newAssessment.id]: { 
              value: Array.isArray(subGoal.answers.options) ? subGoal.answers.options[0] || '' : '', 
              date: '',
              delta: {
                type: currentValueIndex === 0 ? '' : '+',
              }
            }
          }
        )
        });
        
        state.data[goalType].goals[goalId] = {
          ...goal,
          status: 'completed',
          subGoals: updatedSubGoals as any
        };
      } else {
        state.data[goalType].goals[goalId].status = 'completed';
      }
    },
    publishAssessment: (state, action: PayloadAction<{ isPrimary: boolean, activeTab: string, patientId: string }>) => {
      const goalType = action.payload.isPrimary ? 'mainGoal' : 'customGoals';
      // Find the assessment marked as new
      const newAssessment = Object.values(state.data[goalType].assessments)
        .find(assessment => assessment.isNew);
      
      if (newAssessment) {

        state.data[goalType].assessmentTrend = []
        state.data[goalType].assessments[newAssessment.id] = {
          ...newAssessment,
          isNew: false,
          action: newAssessment.isSkip ? "skipped" : "published",
          date: moment(new Date()).format("YYYY-MM-DD"),
        };
      }
    },
    addGoal: (state, action: PayloadAction<{
      name: string;
      type: string;
      tag: string;
      role: string;
      isPrimary: boolean;
    }>) => {
      const goalType = action.payload.isPrimary ? 'mainGoal' : 'customGoals';
      const newId = `goal-${Object.keys(state.data[goalType].goals).length + 1}`;
      const newGoal: Goal = {
        id: newId,
        name: action.payload.name,
        type: action.payload.type,
        status: 'pending',
        isPrimary: 0,
        assessmentIds: [],
        tag: action.payload.tag,
        role: action.payload.role,
      };

      state.data[goalType].goals[newId] = newGoal;
    },
    removeGoal: (state, action: PayloadAction<{ goalId: string; isPrimary: boolean }>) => {
      const { goalId, isPrimary } = action.payload;
      const goalType = isPrimary ? 'mainGoal' : 'customGoals';
      delete state.data[goalType].goals[goalId];
    },
    addGoals: (state, action: PayloadAction<{
      goals: { [key: string]: Goal };
      assessments: { [key: string]: Assessment };
      isPrimary: boolean;
    }>) => {
      const goalType = action.payload.isPrimary ? 'mainGoal' : 'customGoals';
      state.data[goalType].goals = action.payload.goals;
      state.data[goalType].assessments = action.payload.assessments;
    },
    setPreAssessments: (state, action: PayloadAction<{
      preAssessments: any[];
    }>) => {
      
      state.preAssessments = action.payload.preAssessments;
    },
  },
});

export const { addAssessment, markAsSkip, markAsComplete, publishAssessment,  cancelAssessment, addGoal, removeGoal, addGoals, updateSubGoalAssessment, totalAssessmentCount, setGoalsPagination, setActiveAssessmentPrefill, applyActiveAssessmentPrefill, setPreAssessments } = goalSlice.actions;
export default goalSlice.reducer;